import { SkuBackfillResult } from '@/console/api/types'
import { SKU_BACKFILL_OUTCOMES } from '@/console/tabs/fopt/skuBackfill'

/**
 * 백필 결과를 `sessionStorage` 에 남긴다. 잘못 덮어쓴 값을 되돌릴 근거가 응답의 previousSkuId 뿐이라
 * 새로고침으로 잃으면 안 된다. 읽기는 방어적이다 — 모양이 어긋난 값은 버리고 키를 지운다.
 */
export const SKU_BACKFILL_RUNS_STORAGE_KEY = 'moms-console.sku-backfill-runs.v1'
/** 한 번에 최대 100건이라 20회분이면 수백 KB 안쪽이다 — 저장소 한도를 넘지 않는 선에서 넉넉히 둔다. */
export const SKU_BACKFILL_RUNS_KEPT = 20

/** `id` 는 결과 표의 key 다 — 제출 시각은 겹칠 수 있어 따로 둔다. */
export type SkuBackfillRun = { id: string; submittedAt: string; overwrite: boolean; results: SkuBackfillResult[] }

const KNOWN_OUTCOMES = new Set<string>(SKU_BACKFILL_OUTCOMES)

const isOptionalString = (value: unknown): boolean => value === undefined || value === null || typeof value === 'string'

const isBackfillResult = (value: unknown): value is SkuBackfillResult => {
  if (!value || typeof value !== 'object') return false
  const result = value as Record<string, unknown>
  return (
    typeof result.standardOrderProductId === 'number' &&
    typeof result.requestedSkuId === 'string' &&
    typeof result.outcome === 'string' &&
    KNOWN_OUTCOMES.has(result.outcome) &&
    isOptionalString(result.previousSkuId)
  )
}

const isBackfillRun = (value: unknown): value is SkuBackfillRun => {
  if (!value || typeof value !== 'object') return false
  const run = value as Record<string, unknown>
  return (
    typeof run.id === 'string' &&
    typeof run.submittedAt === 'string' &&
    typeof run.overwrite === 'boolean' &&
    Array.isArray(run.results) &&
    run.results.every(isBackfillResult)
  )
}

const clearRuns = (): void => {
  try {
    sessionStorage.removeItem(SKU_BACKFILL_RUNS_STORAGE_KEY)
  } catch {
    /* 저장소를 못 써도 화면은 돌아야 한다 */
  }
}

const readRuns = (): unknown => {
  try {
    const raw = sessionStorage.getItem(SKU_BACKFILL_RUNS_STORAGE_KEY)
    return raw ? JSON.parse(raw) : []
  } catch {
    return null
  }
}

/** 최근 제출이 앞에 온다. */
export const loadSkuBackfillRuns = (): SkuBackfillRun[] => {
  const stored = readRuns()
  if (Array.isArray(stored) && stored.every(isBackfillRun)) return stored
  clearRuns()
  return []
}

export const saveSkuBackfillRuns = (runs: SkuBackfillRun[]): void => {
  try {
    sessionStorage.setItem(SKU_BACKFILL_RUNS_STORAGE_KEY, JSON.stringify(runs))
  } catch {
    /* 저장소를 못 써도 화면은 돌아야 한다 — 이번 결과는 화면에는 남아 있다 */
  }
}
